import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Sprout, Flame, Droplets, Mountain, Palette, GraduationCap, Globe2, HandCoins, Users, BookOpen, Clock, Archive, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import dayjs from "dayjs";
import EcosystemMap from "@/components/EcosystemMap";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

const HERO = "https://images.unsplash.com/photo-1568489711036-9c94a7d5aea6?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_ARCH = "https://images.unsplash.com/photo-1764075895207-eb880b980324?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_MED = "https://images.unsplash.com/photo-1723463533634-42d479628e7e?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_FARM = "https://images.unsplash.com/photo-1783420040194-0bbd06fd6512?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const [email, setEmail] = useState("");
  const [retreats, setRetreats] = useState([]);
  const { t } = useContent("home");

  useEffect(() => {
    api.get("/retreats?status=upcoming").then(r => setRetreats(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success("You are on the list. Welcome home."); setEmail(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Try again"); }
  };

  return (
    <div>
      <PageSEO
        title={"Celestial Seed Sanctuary | Regenerative Sanctuary in Colombia"}
        description={"A regenerative sanctuary in the cloud forests of Cundinamarca, Colombia. Arts, ecology, education, wellness, and community — cultivating living models for future generations."}
      />
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={t("hero.image_url", HERO)} alt="Colombian cloud forest" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest/40 via-forest/20 to-bone" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24">
          <div className="max-w-3xl stagger">
            <div className="text-bone/90 overline mb-6" data-testid="hero-overline">
              {t("hero.overline", "Celestial Seed Sanctuary · Cundinamarca, Colombia")}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-bone leading-[1.05] tracking-tight" data-testid="hero-title">
              {t("hero.title", "A Living Sanctuary. A Practical School. A Regenerative Institution.")}
            </h1>
            <p className="mt-8 text-bone/85 text-lg max-w-2xl leading-relaxed">
              {t("hero.body_primary", "Rooted in the cloud forests of Cundinamarca, Colombia, Celestial Seed Sanctuary is a living experiment in regenerative culture — bringing together ecological stewardship, natural building, food cultivation, healing arts, music and culture, education, community, and practical participation.")}
            </p>
            <p className="mt-4 text-bone/80 max-w-2xl leading-relaxed">
              {t("hero.body_middle", "This is a place being built in real time. People come to learn, contribute, create, restore, teach, exchange skills, and build relationships with the land and one another.")}
            </p>
            <p className="mt-4 text-bone/70 max-w-2xl leading-relaxed">
              {t("hero.body_secondary", "Behind the Sanctuary is NEOS — the New Earth Operating System — the digital and institutional infrastructure connecting our knowledge, people, projects, resources, and evolving work.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/retreats" data-testid="hero-cta-retreats" className="pill pill-accent">
                {t("hero.cta_primary_label", "Explore the Sanctuary")} <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/volunteer" data-testid="hero-cta-participate" className="pill pill-outline text-bone border-bone">
                {t("hero.cta_secondary_label", "Participate")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Institutional Ecosystem Map — living forest, not bureaucracy */}
      <EcosystemMap />

      {/* Ecosystem — Departments of the Institution */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-5">
            <div className="overline mb-3">The Ecosystem</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]">One institution.<br/>Many departments.<br/>All connected.</h2>
          </div>
          <p className="lg:col-span-7 text-ink-soft leading-relaxed self-end">
            Celestial Seed is organized as an emerging institution — the departments below are the working divisions of a single body. Each grows independently while operating through the shared infrastructure of NEOS.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Sprout, t: "The Sanctuary", d: "Land stewardship, food forest, watershed, natural building. The living ground of everything.", to: "/about" },
            { icon: Flame, t: "Rebirthing Genesis", d: "The healing department — breathwork, nervous system regulation, ceremonial integration.", to: "/rebirthing" },
            { icon: Mountain, t: "Cloud Forrest EcoVillage", d: "Housing & regenerative development — bamboo tiny homes, master planning, land expansion.", to: "/dubbi" },
            { icon: GraduationCap, t: "School of Practical Wisdom & Life", d: "Seasonal intensives, apprenticeships, guilds, certifications, the Knowledge Vault.", badge: "Coming Soon" },
            { icon: Palette, t: "Culture & Arts", d: "Artists, musicians, dancers, filmmakers, storytellers, indigenous knowledge keepers, natural builders — culture as the language of the institution.", to: "/culture", badge: "Central" },
            { icon: Globe2, t: "Tribes of Humanity Global Alliance", d: "International relations — the network of healers, teachers, artists, builders and partner sanctuaries." },
            { icon: Droplets, t: "Healer Fellowship", d: "Scholarships & leadership development for practitioners.", badge: "Coming Soon" },
            { icon: Users, t: "Volunteer & Operator Corps", d: "Stewardship, training, residency — real work in exchange for room, board, and belonging.", to: "/volunteer" },
            { icon: HandCoins, t: "Grant & Impact Center", d: "Foundations, sponsors, campaigns, monthly giving, healer scholarships, impact reporting.", to: "/donate" },
            { icon: BookOpen, t: "Media & Publishing Studio", d: "Podcast, books, articles, film, photography, newsletter.", badge: "Coming Soon" },
            { icon: Archive, t: "Archives & Legacy", d: "The institutional memory — founding documents, oral histories, land history, decision archives. The bones of the institution.", badge: "Coming Soon" },
            { icon: Eye, t: "Observatory, Learning & Evaluation", d: "The eyes, ears, and mirror of the institution — impact measurement, story collection, program evaluation, ecological observation, annual reporting.", badge: "Coming Soon" },
          ].map((c) => {
            const Body = (
              <div className="h-full rounded-2xl border border-line bg-surface p-7 hover:shadow-[0_20px_40px_-24px_rgba(44,61,48,0.25)] transition-shadow duration-300 flex flex-col">
                <div className="flex items-start justify-between">
                  <c.icon className="h-5 w-5 text-terracotta" strokeWidth={1.5} />
                  {c.badge && <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${c.badge === "New" ? "bg-terracotta text-bone" : "bg-forest/10 text-forest"}`}>{c.badge}</span>}
                </div>
                <div className="font-serif text-2xl mt-4 text-ink leading-tight">{c.t}</div>
                <p className="mt-3 text-ink-soft text-sm leading-relaxed">{c.d}</p>
                {c.to && <span className="mt-4 text-xs text-forest edlink self-start">Enter →</span>}
              </div>
            );
            return c.to ? (
              <Link key={c.t} to={c.to} data-testid={`dept-${c.t.split(" ")[0].toLowerCase()}`}>{Body}</Link>
            ) : (
              <div key={c.t} data-testid={`dept-${c.t.split(" ")[0].toLowerCase()}`}>{Body}</div>
            );
          })}
        </div>

        {/* Institute for Regenerative Civilization — placeholder */}
        <div className="mt-8 rounded-2xl border border-dashed border-forest/30 bg-forest/[0.03] p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center gap-6" data-testid="dept-irc">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-forest/10 grid place-items-center"><Clock className="h-5 w-5 text-forest" strokeWidth={1.5} /></div>
            <div>
              <div className="overline mb-1">Umbrella Initiative · Coming Soon</div>
              <div className="font-serif text-2xl">Institute for Regenerative Civilization</div>
            </div>
          </div>
          <p className="text-ink-soft text-sm leading-relaxed lg:ml-auto max-w-xl">
            The intellectual and research arm of Celestial Seed and NEOS — future home of long-form publications, policy work, regenerative systems research, and institutional development.
          </p>
        </div>
      </section>

      {/* Upcoming retreats */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-3">Upcoming</div>
            <h2 className="font-serif text-4xl lg:text-5xl">Retreats & Gatherings</h2>
          </div>
          <Link to="/retreats" data-testid="home-view-all-retreats" className="edlink text-sm text-forest">View all</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {(retreats.length ? retreats : Array.from({ length: 3 })).map((r, i) => (
            <div key={r?.id || i} className="group rounded-2xl overflow-hidden border border-line bg-surface" data-testid={`retreat-card-${i}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={r?.image_url || IMG_MED} alt={r?.title || "Retreat"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="overline mb-2">{r?.start_date ? dayjs(r.start_date).format("MMM D, YYYY") : "Coming soon"}</div>
                <h3 className="font-serif text-2xl leading-tight">{r?.title || "A New Retreat Awaits"}</h3>
                <p className="text-ink-soft text-sm mt-3 line-clamp-3">{r?.description || "Details will be announced shortly."}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Institutional band */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-3xl overflow-hidden">
          <img src={IMG_ARCH} alt="Bamboo architecture" className="w-full aspect-[4/5] object-cover" />
        </div>
        <div>
          <div className="overline mb-4">The Institution</div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]">Not a retreat business. A civilization model in practice.</h2>
          <p className="text-ink-soft mt-6 leading-relaxed">
            Celestial Seed operates as a university, a foundation, a research institute, and a regenerative sanctuary — combined. Every department below is a working wing of one body, and every person who arrives — student, volunteer, artist, donor, teacher, healer — enters the same ecosystem through a different door.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6">
            {[
              { n: "5", l: "Pillars" },
              { n: "13", l: "Departments" },
              { n: "1", l: "Operating System" },
            ].map(s => (
              <div key={s.l} className="border-t border-line pt-4">
                <div className="font-serif text-4xl text-terracotta leading-none">{s.n}</div>
                <div className="overline mt-2">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/about" data-testid="home-institution-link" className="pill pill-primary">Explore the Institution <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-3xl bg-forest text-bone p-10 lg:p-16 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden grain">
          <div>
            <div className="overline text-bone/70 mb-4">Newsletter</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight">Letters from the mountain.</h2>
            <p className="text-bone/80 mt-4">Retreat announcements, land updates, and slow words from the sanctuary. No noise.</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-3 items-center relative" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
              data-testid="newsletter-email"
              className="flex-1 bg-transparent border-b border-bone/50 focus:border-bone outline-none py-4 text-lg placeholder:text-bone/50 text-bone" />
            <button data-testid="newsletter-submit" className="pill pill-accent">Subscribe</button>
          </form>
        </div>
      </section>
    </div>
  );
}
